/**
 * FleetScheduler — greedy chronological assignment of orders to trucks and drivers.
 * Enforces DOT HOS rules, maintenance blackouts, PTO, and vehicle prioritization.
 * Ported from schedule-3.js.
 */

// ============ Interfaces ============

export interface SchedulerDriver {
  id: string;
  homeBase: string;
  workDays: number[];
  shift: "AM" | "PM";
  currentLocation: string;
  availableTime: string | null;
  shiftStartTime: string | null;
  hoursDrivenToday: number;
  currentTruckId: string | null;
}

export interface SchedulerTruck {
  id: string;
  currentLocation: string;
  availableTime: string | null;
  priority: number;
}

export interface SchedulerOrder {
  id: string;
  startLocation: string;
  arrivalLocation: string;
  startTime: string;
  arrivalTime: string;
  assignedDriverId?: string;
  assignedTruckId?: string;
}

export interface ServiceHold {
  truckId: string;
  startTime: string;
  durationHours: number;
}

export interface DayOff {
  driverId: string;
  date: string; // YYYY-MM-DD
}

export interface ScheduleResult {
  scheduledOrders: SchedulerOrder[];
  unassignedOrders: SchedulerOrder[];
  finalDriverStates: SchedulerDriver[];
  finalTruckStates: SchedulerTruck[];
}

// ============ Class ============

export class FleetScheduler {
  private trucks: SchedulerTruck[];
  private drivers: SchedulerDriver[];
  private turnTimeHours: number;
  private serviceHolds: ServiceHold[];
  private daysOff: DayOff[];

  constructor(
    trucks: SchedulerTruck[],
    drivers: SchedulerDriver[],
    turnTimeInHours = 1.0,
    serviceHolds: ServiceHold[] = [],
    daysOff: DayOff[] = []
  ) {
    this.trucks = JSON.parse(JSON.stringify(trucks));
    this.drivers = JSON.parse(JSON.stringify(drivers));
    this.turnTimeHours = turnTimeInHours;
    this.serviceHolds = JSON.parse(JSON.stringify(serviceHolds));
    this.daysOff = JSON.parse(JSON.stringify(daysOff));

    // Hydrate driver state
    for (const driver of this.drivers) {
      driver.hoursDrivenToday = driver.hoursDrivenToday || 0.0;
      driver.shiftStartTime = driver.shiftStartTime || null;
      driver.currentTruckId = driver.currentTruckId || null;
      driver.currentLocation = driver.currentLocation || driver.homeBase;
      driver.availableTime = driver.availableTime || null;
    }

    // Hydrate truck state
    for (const truck of this.trucks) {
      truck.availableTime = truck.availableTime || null;
      truck.priority = truck.priority || 999;
    }
  }

  // ==========================================
  // HELPER METHODS
  // ==========================================

  private getHoursBetween(startIso: string | null, endIso: string | null): number {
    if (!startIso || !endIso) return 0;
    return (new Date(endIso).getTime() - new Date(startIso).getTime()) / (1000 * 60 * 60);
  }

  private addHoursToIso(isoString: string, decimalHours: number): string {
    const date = new Date(isoString);
    date.setTime(date.getTime() + decimalHours * 60 * 60 * 1000);
    return date.toISOString();
  }

  private intervalsOverlap(startIso1: string, endIso1: string, startIso2: string, endIso2: string): boolean {
    const s1 = new Date(startIso1).getTime();
    const e1 = new Date(endIso1).getTime();
    const s2 = new Date(startIso2).getTime();
    const e2 = new Date(endIso2).getTime();
    return s1 < e2 && s2 < e1;
  }

  private getLocalDateString(dateObj: Date): string {
    const year = dateObj.getFullYear();
    const month = String(dateObj.getMonth() + 1).padStart(2, "0");
    const day = String(dateObj.getDate()).padStart(2, "0");
    return `${year}-${month}-${day}`;
  }

  // ==========================================
  // ELIGIBILITY
  // ==========================================

  private isDriverEligible(driver: SchedulerDriver, order: SchedulerOrder): boolean {
    const orderStart = new Date(order.startTime);
    const dayOfWeek = orderStart.getDay();
    const hourOfDay = orderStart.getHours();

    // RULE 1: Schedule & PTO
    if (!driver.workDays.includes(dayOfWeek)) return false;
    const localDateStr = this.getLocalDateString(orderStart);
    if (this.daysOff.some((d) => d.driverId === driver.id && d.date === localDateStr)) return false;

    // RULE 2: Shifts (AM: 05:00–16:59)
    const isAMTime = hourOfDay >= 5 && hourOfDay < 17;
    if (driver.shift === "AM" && !isAMTime) return false;
    if (driver.shift === "PM" && isAMTime) return false;

    // RULE 3: Location & Availability
    if (driver.currentLocation !== order.startLocation) return false;
    if (driver.availableTime && new Date(driver.availableTime) > orderStart) return false;

    // RULE 4: 10-Hour Reset
    const hoursSinceLastActivity = driver.availableTime
      ? this.getHoursBetween(driver.availableTime, order.startTime)
      : 10;

    if (!driver.availableTime || hoursSinceLastActivity >= 10) {
      driver.hoursDrivenToday = 0.0;
      driver.shiftStartTime = order.startTime;
    } else if (!driver.shiftStartTime) {
      driver.shiftStartTime = order.startTime;
    }

    // RULE 5: 11-Hour Driving Limit
    const tripDuration = this.getHoursBetween(order.startTime, order.arrivalTime);
    if (driver.hoursDrivenToday + tripDuration > 11) return false;

    // RULE 6: 14-Hour Shift Limit
    const shiftDurationAtArrival = this.getHoursBetween(driver.shiftStartTime, order.arrivalTime);
    if (shiftDurationAtArrival > 14) return false;

    // RULE 7: Anti-Stranding
    if (driver.currentLocation !== driver.homeBase) {
      if (order.arrivalLocation !== driver.homeBase) return false;
    }

    return true;
  }

  private isTruckEligible(truck: SchedulerTruck, order: SchedulerOrder): boolean {
    // Physics
    if (truck.currentLocation !== order.startLocation) return false;
    if (truck.availableTime && new Date(truck.availableTime) > new Date(order.startTime)) return false;

    // Service Hold Check
    const orderEndWithTurn = this.addHoursToIso(order.arrivalTime, this.turnTimeHours);
    const truckHolds = this.serviceHolds.filter((h) => h.truckId === truck.id);

    for (const hold of truckHolds) {
      const holdEnd = this.addHoursToIso(hold.startTime, hold.durationHours);
      if (this.intervalsOverlap(order.startTime, orderEndWithTurn, hold.startTime, holdEnd)) {
        return false;
      }
    }

    return true;
  }

  // ==========================================
  // MATCHING ENGINE
  // ==========================================

  scheduleOrders(orders: SchedulerOrder[]): ScheduleResult {
    const sortedOrders = [...orders].sort(
      (a, b) => new Date(a.startTime).getTime() - new Date(b.startTime).getTime()
    );
    const unassignedOrders: SchedulerOrder[] = [];

    for (const order of sortedOrders) {
      const eligibleTrucks = this.trucks.filter((t) => this.isTruckEligible(t, order));
      const eligibleDrivers = this.drivers.filter((d) => this.isDriverEligible(d, order));

      // Prioritize trucks
      eligibleTrucks.sort((a, b) => a.priority - b.priority);

      let assigned = false;

      for (const driver of eligibleDrivers) {
        const selectedTruck =
          eligibleTrucks.find((t) => t.id === driver.currentTruckId) || eligibleTrucks[0];

        if (selectedTruck) {
          const nextAvailableTimeIso = this.addHoursToIso(order.arrivalTime, this.turnTimeHours);

          order.assignedDriverId = driver.id;
          order.assignedTruckId = selectedTruck.id;

          driver.currentLocation = order.arrivalLocation;
          driver.availableTime = nextAvailableTimeIso;
          driver.hoursDrivenToday += this.getHoursBetween(order.startTime, order.arrivalTime);
          driver.currentTruckId = selectedTruck.id;

          selectedTruck.currentLocation = order.arrivalLocation;
          selectedTruck.availableTime = nextAvailableTimeIso;

          assigned = true;
          break;
        }
      }

      if (!assigned) {
        unassignedOrders.push(order);
      }
    }

    return {
      scheduledOrders: sortedOrders.filter((o) => o.assignedDriverId),
      unassignedOrders,
      finalDriverStates: this.drivers,
      finalTruckStates: this.trucks,
    };
  }
}
